import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ColumnDef,
  OnChangeFn,
  flexRender,
  useReactTable,
  getCoreRowModel,
  ColumnOrderState,
} from '@tanstack/react-table';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import { Button } from 'ncoded-component-library';
import useQueryParams from 'hooks/useQueryParams';
import { ReorderResizeTableProps, ResCellContext, RowType } from './types';
import SortIcon from 'icons/Sort.icon';
import DraggableCard from 'components/DraggableCard';
import ResolvePagination from 'components/ResolvePagination';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { hasSettings, isSortableCol } from './utils';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import TableRowPlaceholder from './components/TableRowPlaceholder';
import utils, {
  getIsSortedDescBy,
  getIsSortApplied,
  getSortValue,
  getArrayLength,
} from 'utils';
import TableEmptyState from './components/TableEmptyState';
import useMatchMedia from 'hooks/useMatchMedia';
import AdjustmentsIcon from 'icons/Adjustments.icon';
import useHorizontalScrollFade from 'hooks/useHorizontalScrollFade';
import ColumnSortFilterDropdown from 'components/ColumnSortFilterDropdown';
import {
  DEFAULT_LIMIT,
  LIMIT_PARAM_KEYWORD,
  PAGE_PARAM_KEYWORD,
} from 'data/global';

import './ReorderResizeTable.styles.scss';

const DEFAULT_COL = {
  size: 200, //starting column size
  minSize: 50, //enforced during column resizing
  maxSize: 1000, //enforced during column resizing
};

const DRAG_START_DISTANCE = 6;

function ReorderResizeTable<T extends Record<string, any>>(
  props: ReorderResizeTableProps<T>,
) {
  const { params, setQueryParam } = useQueryParams();

  const { sort: paramsSort } = params || {};

  const {
    className,
    data,
    columns,
    headingTranslations,
    tableContentTransformations,
    sortingValues,
    columnFilterOptions,
    settingsValues,
    deafaultTableColSize,
    customSizes,
    tableHeaderContentTransformations,
    isDragDisabled,
    isResizeDisabled,
    emptyMessage,
    pageParam,
    totalPages = 1,
    totalItems,
    limit,
    numberOfPlaceholderRows = 10,
    showPlaceholder = true,
    isDataLoading = false,
    hasHorizontalScrollFade = true,
    recordName = 'result',
    columnResizeMode = 'onChange',
    columnResizeDirection = 'ltr',
    sort = paramsSort,
    columnSizing,
    onColumnSizingChange,
    onReorder,
    getRowId,
    getIsRowExpanded,
    getIsRowSelected,
    getRowBackgroundColor,
    getColSpan,
    renderExpandedContent,
    onRowClick,
    onRowDoubleClick,
    onPaginationClick,
    updateSort = (value: string) => setQueryParam('sort', value),
  } = props;

  const numberOfPlaceholderRowsToRender = useMemo(() => {
    const numberOfRows = getArrayLength(data);

    if (
      totalPages === 1 &&
      !!numberOfRows &&
      numberOfRows < numberOfPlaceholderRows
    )
      return numberOfRows;

    return numberOfPlaceholderRows;
  }, [data, numberOfPlaceholderRows, totalPages]);

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const reorderCB: OnChangeFn<ColumnOrderState> = useCallback(
    (v) => onReorder?.(v as string[]),
    [onReorder],
  );

  const headingTranslationsMap = useMemo(
    () =>
      headingTranslations
        ? Object.entries(headingTranslations).reduce(
            (acc, [key, val]) => ({ ...acc, [key]: t(val) }),
            {} as Record<keyof T, string>,
          )
        : columns.reduce(
            (acc, col) => ({ ...acc, [col]: t(col as string) }),
            {} as Record<keyof T, string>,
          ),
    [headingTranslations, columns, t],
  );

  // Use refs for the header and cell transformations so that column definitions
  // (and the header/cell functions inside them) stay stable when only the
  // transformations change.
  // This is critical because TanStack's flexRender treats those functions
  // as React components — a new function reference causes unmount/remount,
  // which resets cell state such as an avatar's loading placeholder.
  const tableHeaderTransformationsRef = useRef(
    tableHeaderContentTransformations,
  );
  tableHeaderTransformationsRef.current = tableHeaderContentTransformations;

  const tableContentTransformationsRef = useRef(tableContentTransformations);
  tableContentTransformationsRef.current = tableContentTransformations;

  const cellRenderersRef = useRef<
    Record<string, (info: ResCellContext<RowType, string>) => React.ReactNode>
  >({});

  const getCellRenderer = useCallback((col: string) => {
    if (!cellRenderersRef.current[col])
      cellRenderersRef.current[col] = (info) =>
        tableContentTransformationsRef.current[col]?.(info.lead, info.config);

    return cellRenderersRef.current[col];
  }, []);

  const tanstackColumns = useMemo(
    () =>
      (columns as string[]).map((col) => ({
        header: (() => {
          const transform = tableHeaderTransformationsRef.current?.[col];
          if (!transform) return headingTranslationsMap[col];
          if (typeof transform !== 'function') return transform;
          // Stable wrapper that delegates to the latest header transformation via ref.
          // This prevents column recreation when only headers change (e.g. isAllSelected).
          return (val?: any) => {
            const current = tableHeaderTransformationsRef.current?.[col];
            return typeof current === 'function' ? current(val) : current;
          };
        })(),
        accessorKey: col,
        // Stable wrapper that delegates to the latest cell transformation via
        // the ref, so re-rendering a row never re-creates its cell components.
        cell: getCellRenderer(col),
        ...(customSizes?.[col] ? { size: customSizes[col] } : {}),
      })) as unknown as ColumnDef<T>[],
    [columns, headingTranslationsMap, customSizes, getCellRenderer],
  );

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    columnResizeMode,
    columnResizeDirection,
    getCoreRowModel: getCoreRowModel(),
    ...(getRowId && { getRowId }),
    getIsRowExpanded,
    initialState: {
      columnOrder: columns as string[],
      columnSizing: columnSizing ?? {},
    },
    state: {
      columnOrder: columns as string[],
    },
    onColumnOrderChange: reorderCB,
    defaultColumn: deafaultTableColSize || DEFAULT_COL,
  });

  const isResizingColumn = table.getState().columnSizingInfo.isResizingColumn;
  const liveColumnSizing = table.getState().columnSizing;

  const wasResizingColumnRef = useRef(isResizingColumn);

  useEffect(() => {
    if (wasResizingColumnRef.current && !isResizingColumn)
      onColumnSizingChange?.(liveColumnSizing);

    wasResizingColumnRef.current = isResizingColumn;
  }, [isResizingColumn, liveColumnSizing, onColumnSizingChange]);

  const [currentlyDragged, setCurrentlyDragged] = useState('');

  const shouldSkipNextSortRef = useRef(false);

  const numberOfColumns = table.getAllColumns().length;

  const numberOfRows = table.getRowCount();

  const showFade = hasHorizontalScrollFade && !isDataLoading;

  const { showRightFade, scrollContainerRef } = useHorizontalScrollFade(
    showFade,
    [data, columns, isDataLoading, numberOfRows],
  );

  const isEmptyTable = useMemo(() => !numberOfRows, [numberOfRows]);

  const shouldShowPlaceholder = isDataLoading && showPlaceholder;

  const resultsCount = useMemo(() => {
    if (!totalItems || isEmptyTable || shouldShowPlaceholder) return null;

    const currentPage = totalItems
      ? +params[pageParam || PAGE_PARAM_KEYWORD] || 1
      : 1;

    const pageLimit = limit || +params[LIMIT_PARAM_KEYWORD] || +DEFAULT_LIMIT;

    const effectiveTotalItems = totalItems || numberOfRows;

    return {
      from: (currentPage - 1) * pageLimit + 1,
      to: Math.min(currentPage * pageLimit, effectiveTotalItems),
      totalItems: effectiveTotalItems,
    };
  }, [
    isEmptyTable,
    shouldShowPlaceholder,
    totalItems,
    limit,
    numberOfRows,
    params,
    pageParam,
  ]);

  const handleDragStart = useCallback((e) => {
    shouldSkipNextSortRef.current = true;
    setCurrentlyDragged(e.active.id as string);
  }, []);

  const clearDragState = useCallback(() => {
    setCurrentlyDragged('');
    setTimeout(() => {
      shouldSkipNextSortRef.current = false;
    }, 0);
  }, []);

  const onDragEnd = useCallback(
    async (e: DragEndEvent) => {
      const { active, over } = e;

      clearDragState();

      if (!over || active.id === over.id) return;

      const oldIndex = columns.findIndex((old) => old === active.id);
      const newIndex = columns.findIndex((old) => old === over.id);

      await onReorder?.(arrayMove(columns, oldIndex, newIndex) as any);
    },
    [columns, onReorder, clearDragState],
  );

  const onSort = useCallback(
    (id: string, isDesc: boolean) => {
      if (shouldSkipNextSortRef.current) {
        shouldSkipNextSortRef.current = false;
        return;
      }

      const sortingValue = sortingValues[id];

      updateSort(getSortValue(sortingValue, !isDesc));
    },
    [sortingValues, updateSort],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: DRAG_START_DISTANCE,
      },
    }),
  );

  const classes = classNames(
    'reorder-resize-table',
    { 'is-loading': isDataLoading, empty: isEmptyTable },
    className,
  );

  if (isPhablet && isEmptyTable && !isDataLoading)
    return <TableEmptyState emptyMessage={emptyMessage} />;

  return (
    <div className="reorder-resize-table__wrapper">
      <div
        className={classes}
        style={{ direction: table.options.columnResizeDirection }}
      >
        <DndContext
          collisionDetection={closestCenter}
          sensors={sensors}
          onDragEnd={onDragEnd}
          onDragStart={handleDragStart}
          onDragCancel={clearDragState}
          modifiers={[restrictToHorizontalAxis]}
        >
          <div
            className={classNames('horizontal-scroll-fade', {
              'header-fade-right': showRightFade,
            })}
          >
            <div ref={scrollContainerRef} className="overflow-x-auto">
              <table
                {...(!isEmptyTable && {
                  style: {
                    width: table.getCenterTotalSize(),
                  },
                })}
              >
                <thead>
                  {shouldShowPlaceholder ? (
                    <TableRowPlaceholder numberOfColumns={numberOfColumns} />
                  ) : (
                    table.getHeaderGroups().map((headerGroup) => (
                      <tr
                        key={headerGroup.id}
                        className={classNames({
                          'something-being-dragged': !!currentlyDragged,
                        })}
                      >
                        <SortableContext
                          items={columns as string[]}
                          strategy={horizontalListSortingStrategy}
                        >
                          {headerGroup.headers.map((header) => {
                            const currSort = sortingValues?.[header.id];
                            const isSelected = getIsSortApplied(
                              sort,
                              sortingValues?.[header.id],
                            );

                            const isDesc = getIsSortedDescBy(sort, currSort);

                            const isSortable =
                              !currentlyDragged &&
                              isSortableCol(header.id, sortingValues);

                            const hasColumnDropdown =
                              !isPhablet &&
                              !!columnFilterOptions &&
                              (isSortableCol(header.id, sortingValues) ||
                                !!columnFilterOptions[header.id]);

                            const floatingCardContent = (
                              <div
                                className={classNames('floating-card', {
                                  clickable: hasColumnDropdown || isSortable,
                                  grabbable: !isDragDisabled && !isEmptyTable,
                                  'currently-dragged':
                                    currentlyDragged === header.id,
                                })}
                                {...(!hasColumnDropdown &&
                                  isSortable && {
                                    onClick: () => onSort(header.id, isDesc),
                                  })}
                              >
                                {!header.isPlaceholder &&
                                  flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                              </div>
                            );

                            return (
                              <th
                                key={header.id}
                                {...{
                                  colSpan: header.colSpan,
                                  style: {
                                    width: header.getSize(),
                                  },
                                }}
                              >
                                <div
                                  className={classNames(header.id, {
                                    selected: isSelected,
                                  })}
                                >
                                  <DraggableCard
                                    key={header.id}
                                    id={header.id}
                                    disabled={isDragDisabled || isEmptyTable}
                                  >
                                    {hasColumnDropdown ? (
                                      <ColumnSortFilterDropdown
                                        sortingValue={
                                          sortingValues?.[header.id]
                                        }
                                        filterOption={
                                          columnFilterOptions[header.id]
                                            ?.options
                                        }
                                        hasCheckboxAll={
                                          columnFilterOptions[header.id]
                                            ?.hasCheckboxAll
                                        }
                                        sortDirectionTranslation={
                                          columnFilterOptions[header.id]
                                            ?.sortDirectionTranslation
                                        }
                                        sort={sort}
                                        updateSort={updateSort}
                                        isDisabled={!!currentlyDragged}
                                      >
                                        {floatingCardContent}
                                      </ColumnSortFilterDropdown>
                                    ) : (
                                      floatingCardContent
                                    )}
                                  </DraggableCard>

                                  {!currentlyDragged &&
                                    !hasColumnDropdown &&
                                    isSortableCol(header.id, sortingValues) && (
                                      <Button
                                        disabled={isEmptyTable}
                                        onClick={() => {
                                          const sortingValue =
                                            sortingValues[header.id];

                                          updateSort(
                                            getSortValue(sortingValue, !isDesc),
                                          );
                                        }}
                                        className={classNames(
                                          'sorting-button',
                                          {
                                            double: !isSelected,
                                            selected: isSelected,
                                            rotated: !isDesc,
                                          },
                                        )}
                                      >
                                        {isSelected ? (
                                          <ArrowdownIcon />
                                        ) : (
                                          <>
                                            <SortIcon />
                                            <SortIcon />
                                          </>
                                        )}
                                      </Button>
                                    )}

                                  {!currentlyDragged &&
                                    hasSettings(header.id, settingsValues) && (
                                      <Button
                                        disabled={isEmptyTable}
                                        onClick={settingsValues[header.id]}
                                        variant="icon"
                                        className="settings-button"
                                        icon={AdjustmentsIcon}
                                      />
                                    )}

                                  {!isResizeDisabled && !isEmptyTable && (
                                    <div
                                      {...{
                                        onDoubleClick: () =>
                                          header.column.resetSize(),
                                        onMouseDown: header.getResizeHandler(),
                                        onTouchStart: header.getResizeHandler(),
                                        className: `resizer ${
                                          table.options.columnResizeDirection
                                        } ${
                                          header.column.getIsResizing()
                                            ? 'isResizing'
                                            : ''
                                        }`,
                                        style: {
                                          transform:
                                            columnResizeMode === 'onEnd' &&
                                            header.column.getIsResizing()
                                              ? `translateX(${
                                                  (table.options
                                                    .columnResizeDirection ===
                                                  'rtl'
                                                    ? -1
                                                    : 1) *
                                                  (table.getState()
                                                    .columnSizingInfo
                                                    .deltaOffset ?? 0)
                                                }px)`
                                              : '',
                                        },
                                      }}
                                    />
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </SortableContext>
                      </tr>
                    ))
                  )}
                </thead>
                <tbody>
                  {shouldShowPlaceholder ? (
                    Array(numberOfPlaceholderRowsToRender)
                      .fill(0)
                      .map((_, i) => (
                        <TableRowPlaceholder
                          key={i}
                          numberOfColumns={numberOfColumns}
                        />
                      ))
                  ) : isEmptyTable ? (
                    <tr>
                      <td colSpan={numberOfColumns}>
                        <TableEmptyState emptyMessage={emptyMessage} />
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => {
                      const original = row.original;

                      const hasAdditionalContent = row.getIsExpanded();
                      const cols = row.getVisibleCells();

                      const backgroundColor = getRowBackgroundColor?.(original);
                      const selected = getIsRowSelected?.(original);

                      let colSpanToSkip = 0;

                      return (
                        <Fragment key={row.id}>
                          <tr
                            className={classNames(backgroundColor, {
                              'expandable-row': hasAdditionalContent,
                              selected,
                              clickable: !!onRowClick,
                            })}
                            onClick={() => onRowClick?.(original)}
                            onDoubleClick={() => onRowDoubleClick?.(original)}
                          >
                            {cols.map((cell) => {
                              const colId = cell.column.id;
                              const colSpan =
                                getColSpan?.(original, colId) || 1;
                              const sortAlreadyApplied = sort?.includes(
                                sortingValues?.[colId],
                              );

                              if (colSpanToSkip > 0) {
                                colSpanToSkip--;
                                return null;
                              }

                              colSpanToSkip += colSpan - 1;

                              return (
                                <td
                                  key={cell.id}
                                  colSpan={colSpan}
                                  className={classNames(colId, {
                                    selected: sortAlreadyApplied,
                                  })}
                                  {...{
                                    style: {
                                      width: cell.column.getSize(),
                                    },
                                  }}
                                >
                                  {flexRender(cell.column.columnDef.cell, {
                                    ...cell.getContext(),
                                    lead: original,
                                  })}
                                </td>
                              );
                            })}
                          </tr>

                          {hasAdditionalContent && (
                            <tr className="expanded-row">
                              <td colSpan={cols.length}>
                                {renderExpandedContent(original)}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DndContext>

        {(resultsCount || totalPages > 1) && (
          <div className="reorder-resize-table__footer">
            <ResolvePagination
              totalPages={totalPages}
              pageParam={pageParam}
              onClick={onPaginationClick}
            />

            {resultsCount && (
              <span className="reorder-resize-table__footer__count">
                {t(
                  resultsCount.totalItems === 1
                    ? 'paginationInfoSingle'
                    : 'paginationInfoRange',
                  {
                    from: utils.getDotedNumber(resultsCount.from),
                    to: utils.getDotedNumber(resultsCount.to),
                    totalItems: utils.getDotedNumber(resultsCount.totalItems),
                    recordName: t(recordName, {
                      count: resultsCount.totalItems,
                    }),
                  },
                )}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReorderResizeTable;
